import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS, COMPANY } from "../../data/content";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 bg-white/95 backdrop-blur-md transition-[box-shadow,border-color] duration-300 ${
          scrolled ? "border-b border-black/10 shadow-[0_1px_0_rgba(0,0,0,0.04)]" : "border-b border-black/5"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 md:h-24 lg:px-8">
          <Logo />
          <nav className="hidden items-center gap-8 lg:flex" aria-label="Navigation principale">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.to === "/" ? "accueil" : link.to.slice(1)}`}
                className={({ isActive }) =>
                  `relative text-[13px] font-semibold uppercase tracking-[0.12em] transition-colors duration-300 ${
                    isActive ? "text-bati-blue" : "text-bati-ink/70 hover:text-bati-blue"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-6 lg:flex">
            <a
              href={COMPANY.phoneHref}
              data-testid="header-phone-link"
              className="flex items-center gap-2 text-sm font-semibold text-bati-ink transition-colors duration-300 hover:text-bati-sky"
            >
              <Phone className="h-4 w-4 text-bati-sky" strokeWidth={2.2} />
              {COMPANY.phone}
            </a>
            <Link
              to="/contact"
              data-testid="header-cta-devis"
              className="bg-bati-blue px-5 py-2.5 text-[13px] font-bold uppercase tracking-[0.1em] text-white transition-colors duration-300 hover:bg-bati-sky"
            >
              Demander un devis
            </Link>
          </div>
          <button
            data-testid="burger-menu-button"
            onClick={() => setOpen(!open)}
            className="grid h-10 w-10 place-items-center text-bati-ink lg:hidden"
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-white px-8 pt-24 lg:hidden"
          >
            <nav className="flex flex-col gap-2">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`mobile-nav-link-${link.to === "/" ? "accueil" : link.to.slice(1)}`}
                    className={({ isActive }) =>
                      `block border-b border-black/10 py-4 font-display text-3xl font-bold tracking-tight ${
                        isActive ? "text-bati-blue" : "text-bati-ink"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.5 }}
              className="mt-8 flex flex-col gap-4"
            >
              <Link
                to="/contact"
                data-testid="mobile-menu-cta-devis"
                className="bg-bati-blue py-4 text-center text-sm font-bold uppercase tracking-[0.12em] text-white"
              >
                Demander un devis
              </Link>
              <a href={COMPANY.phoneHref} className="text-center text-sm font-semibold text-bati-ink">
                {COMPANY.phone}
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
